import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";
import type { OpsPaperworkAttachment } from "@/lib/opsPaperworkAttachmentsClient";
import type { DealTypeMatchCatalog, DealTypeMatchState } from "@/lib/opsDealTypes/dealTypeMatch";

export type DealRow = {
  id: string;
  display_name: string | null;
  status: string;
  paperwork_total?: number;
  paperwork_done?: number;
  paperwork_missing?: number;
  completeness?: number | null;
  metadata?: Record<string, unknown> | null;
  salesperson_name?: string | null;
  salesperson_key?: string | null;
  office_name?: string | null;
  office_key?: string | null;
  type_match_status?: string | null;
  type_match_product_name?: string | null;
  type_match_sale_name?: string | null;
  created_at?: string | null;
  serial?: string | null;
};

export type PaperItem = {
  id: string;
  item_key: string;
  label: string;
  status: string;
  notes: string;
  evidence_url: string | null;
  due_at: string | null;
  packet_layer?: string | null;
  required?: boolean | null;
};

export type DealLinkedUnit = {
  id: string;
  organization_id: string;
  serial: string | null;
  sale_path: string | null;
  current_phase_id: string | null;
  current_phase_name: string | null;
  is_build_complete: boolean;
  build_completed_at: string | null;
  staged_at: string | null;
  retail_app_manufacturer: boolean;
  needs_build_before_ready: boolean;
  build_ready: boolean;
  can_mark_ready_to_deliver: boolean;
};

export type IntEvent = {
  id: string;
  source?: string | null;
  event_type: string;
  deal_id: string | null;
  processing_status: string;
  created_at: string;
};

export type OrgTask = {
  id: string;
  title: string;
  body: string;
  status: string;
  assignee_user_id: string;
  issuer_user_id: string;
  due_at: string | null;
  deal_id: string | null;
};

export type TimelineEventRow = {
  id: string;
  event_type: string;
  body: string;
  created_at: string;
  actor_user_id: string | null;
};

export type DealSalespersonState = {
  host: {
    name: string;
    email: string;
    account_id: string;
    dealer_name: string;
    dealer_id: string;
    dealer_city: string;
    dealer_state: string;
  };
  status: "linked" | "missing" | "invite_requested" | "invited";
  profile: { id: string; email: string | null; full_name: string | null; display_name: string | null } | null;
  invite_org_id: string;
  invite_org_name: string;
  location_id: string | null;
  location_name: string | null;
  can_invite: boolean;
  can_add_location?: boolean;
  on_location?: boolean;
  invite_request: { requested_by: string; requested_at: string } | null;
};

export type DealDetail = {
  deal: Record<string, unknown>;
  paperwork_items: PaperItem[];
  tasks: OrgTask[];
  external_ids: unknown[];
  timeline_events: TimelineEventRow[];
  attachments?: OpsPaperworkAttachment[];
  salesperson?: DealSalespersonState | null;
  deal_type_match?: DealTypeMatchState | null;
  deal_type_catalog?: DealTypeMatchCatalog | null;
  linked_unit?: DealLinkedUnit | null;
};

export function formatTaskAssigneeOption(m: LeadTransferCandidate): string {
  const n = (m.full_name || m.display_name || "").trim();
  if (n) return `${n}${m.email ? ` (${m.email})` : ""}`;
  return m.email || m.user_id;
}

